/**
 * PaxBreakdownRail — pravý kolabovatelný rail s PAX rozkladem vybraného termínu (kumulativní výsledek při prodeji
 * právě N osob). Zvýrazňuje zlom do zisku a aktuálně prodaný počet. Pouze Tailwind `phx-*` tokeny.
 */
import { BarChart3, Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { CalculationTermDto } from '@/api/generated/schemas';
import { cn } from '@/shared/lib/utils';
import { Money, Table, TableCell, TableHeadCell, TableRow } from '@/shared/ui';
import { buildPaxRows, type TermMetrics } from '../model/calc';

interface PaxBreakdownRailProps {
  selected: { term: CalculationTermDto; metrics: TermMetrics } | null;
  open: boolean;
  onToggle: () => void;
}

export function PaxBreakdownRail({ selected, open, onToggle }: PaxBreakdownRailProps) {
  const { t } = useTranslation('venua-planner');

  if (!open) {
    return (
      <button
        type="button"
        onClick={onToggle}
        title={t('calculation.pax.expand')}
        className="border-phx-border-2 bg-phx-surface shadow-phx-xs text-phx-text-2 hover:border-phx-primary hover:text-phx-primary rounded-phx flex h-full w-[42px] cursor-pointer flex-col items-center gap-3 border py-3 transition-colors"
      >
        <ChevronLeft size={15} />
        <span className="text-phx-xs [transform:rotate(180deg)] font-semibold tracking-[0.1em] uppercase [writing-mode:vertical-rl]">
          {t('calculation.pax.title')}
        </span>
        <BarChart3 size={15} />
      </button>
    );
  }

  return (
    <div className="border-phx-border-2 bg-phx-surface rounded-phx flex h-full w-[428px] flex-col overflow-hidden border">
      <div className="border-phx-border bg-phx-surface-2 flex items-center gap-2 border-b px-3 py-2">
        <button
          type="button"
          onClick={onToggle}
          title={t('calculation.pax.collapse')}
          className="text-phx-text-muted hover:text-phx-text rounded-phx-sm inline-flex h-6 w-6 cursor-pointer items-center justify-center border-none bg-transparent transition-colors"
        >
          <ChevronRight size={15} />
        </button>
        <span className="text-phx-text-2 text-phx-xs font-semibold tracking-wide uppercase">
          {t('calculation.pax.title')}
        </span>
      </div>

      {!selected ? (
        <div className="gap-phx-3 text-phx-text-muted flex flex-1 flex-col items-center justify-center px-7 py-6 text-center">
          <Calendar size={30} className="text-phx-text-faint" />
          <div className="text-phx-text-2 text-phx-sm font-semibold">
            {t('calculation.pax.noSelectionTitle')}
          </div>
          <div className="text-phx-xs leading-relaxed">{t('calculation.pax.noSelection')}</div>
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-auto">
          <Table density="dense">
            <Table.Head>
              <TableRow>
                <TableHeadCell density="dense">{t('calculation.pax.col.person')}</TableHeadCell>
                <TableHeadCell density="dense" className="text-right">
                  {t('calculation.pax.col.price')}
                </TableHeadCell>
                <TableHeadCell density="dense" className="text-right">
                  {t('calculation.pax.col.cost')}
                </TableHeadCell>
                <TableHeadCell density="dense" className="text-right">
                  {t('calculation.pax.col.profit')}
                </TableHeadCell>
                <TableHeadCell density="dense" className="text-right">
                  {t('calculation.pax.col.margin')}
                </TableHeadCell>
              </TableRow>
            </Table.Head>
            <Table.Body>
              {buildPaxRows(selected.term, selected.metrics).map((row) => (
                <TableRow
                  key={row.n}
                  className={cn(
                    row.positive ? 'bg-phx-success-tint' : 'bg-phx-danger-tint',
                    row.isCurrent && 'outline-phx-text outline outline-1',
                    (row.isBreakeven || row.isCurrent) && 'font-semibold'
                  )}
                >
                  <TableCell density="dense" className="tabular-nums">
                    {row.n}
                  </TableCell>
                  <TableCell density="dense" className="text-right tabular-nums">
                    <Money value={row.price} decimals={0} />
                  </TableCell>
                  <TableCell
                    density="dense"
                    className="text-phx-text-muted text-right tabular-nums"
                  >
                    <Money value={row.cost} decimals={0} />
                  </TableCell>
                  <TableCell
                    density="dense"
                    className={cn(
                      'text-right font-semibold tabular-nums',
                      row.positive ? 'text-phx-success' : 'text-phx-danger'
                    )}
                  >
                    <Money value={row.profit} decimals={0} />
                  </TableCell>
                  <TableCell
                    density="dense"
                    className={cn(
                      'text-right tabular-nums',
                      row.positive ? 'text-phx-success' : 'text-phx-danger'
                    )}
                  >
                    {row.margin.toFixed(1).replace('.', ',')} %
                  </TableCell>
                </TableRow>
              ))}
            </Table.Body>
          </Table>
        </div>
      )}
    </div>
  );
}
